import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../components/feature/PageLayout';
import PageHero from '../../../components/feature/PageHero';

export default function FullDayWorkshopPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags
    document.title = 'Full Day Workshop: Transforming Your Work with AI | Evernorth AI';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '7-hour comprehensive AI training workshop for organizations. Deep dive into AI strategy, implementation, and team alignment. Starting at $3,000 for 10 attendees in Northwest Georgia.');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/workshops/full-day`);
    }

    // Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "Full Day Workshop: Transforming Your Work with AI",
      "description": "Designed for organizations that want to take AI seriously. A full day of clear, practical explanations with deeper discussion, hands-on practice, and space to address questions and concerns.",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Evernorth AI",
        "areaServed": "Northwest Georgia"
      },
      "url": `${import.meta.env.VITE_SITE_URL}/workshops/full-day`,
      "courseMode": "blended",
      "duration": "PT7H",
      "timeRequired": "PT7H",
      "educationalLevel": "Beginner to Advanced",
      "audience": {
        "@type": "EducationalAudience",
        "educationalRole": "Business leaders and staff"
      },
      "offers": {
        "@type": "Offer",
        "price": "3000",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "description": "Starting price for 10 attendees"
      },
      "hasCourseInstance": {
        "@type": "CourseInstance",
        "courseMode": [
          "onsite",
          "online"
        ],
        "instructor": {
          "@type": "Person",
          "name": "Evernorth AI Team"
        }
      }
    });
    document.head.appendChild(schemaScript);

    return () => {
      document.head.removeChild(schemaScript);
    };
  }, []);

  return (
    <PageLayout>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://qymceftywmiiwdrmhnwb.supabase.co/storage/v1/object/public/files/evernorth-ai/workshops/full-day-workshop.jpg"
            alt="Full Day Workshop: Transforming Your Work with AI"
            title="Full Day AI Workshop for business transformation"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/75 to-navy/90"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-3xl">
            <div className="inline-block mb-4 px-4 py-2 bg-gold/20 border border-gold/30 rounded-md">
              <span className="text-gold text-base font-semibold">Foundations Program</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-white">
              Full Day Workshop
              <br />
              <span className="text-gold">Transforming Your Work with AI</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-8 max-w-2xl">
              Designed for organizations that want to take AI seriously without rushing into tools, systems, or decisions they may later regret.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a
                href="https://calendly.com/evernorthai/full-day-workshop"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3.5 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap"
              >
                Book This Workshop
              </a>
              <div className="flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <i className="ri-time-line text-gold text-lg"></i>
                <span className="text-white font-medium text-sm whitespace-nowrap">7 Hours</span>
              </div>
              <div className="flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <i className="ri-money-dollar-circle-line text-gold text-lg"></i>
                <span className="text-white font-medium text-sm whitespace-nowrap">Starting at $3,000</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-3">
              <div className="inline-block mb-4 px-4 py-2 bg-gold/10 rounded-md">
                <span className="text-navy text-base font-semibold">Overview</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6">
                A Full Day of Clarity, <span className="text-gold">Confidence &amp; Alignment</span>
              </h2>
              <div className="space-y-5 text-charcoal leading-relaxed">
                <p>
                  Over the course of a full day, participants are guided through clear, practical explanations of what AI is, how it applies to real workplace tasks, and how it should be used responsibly.
                </p>
                <p>
                  The additional time allows for deeper discussion, even more hands‑on practice, shared understanding across roles, and space to address questions, concerns, and misconceptions that often go unresolved in shorter sessions.
                </p>
                <p>
                  This workshop is especially valuable for teams that want alignment. Leaders and staff leave with a common language around AI, a realistic understanding of its strengths and limitations, and greater confidence in using it thoughtfully in everyday work.
                </p>
                <p>
                  The focus remains on education and clarity, so organizations can begin using AI daily to improve their processes and reduce stress.
                </p>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-5">
              <div className="bg-gold/10 border border-gold/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/20 rounded-lg">
                    <i className="ri-check-double-line text-gold text-xl"></i>
                  </div>
                  <h4 className="text-navy font-bold text-lg">Ready the Next Day</h4>
                </div>
                <p className="text-charcoal text-sm leading-relaxed">
                  Attendees walk out of the workshop with skills ready to use <strong>the next day</strong>. No technical background is required.
                </p>
              </div>

              <div className="bg-navy/5 border border-navy/10 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-navy/10 rounded-lg">
                    <i className="ri-team-line text-navy text-xl"></i>
                  </div>
                  <h4 className="text-navy font-bold text-lg">Team Alignment</h4>
                </div>
                <p className="text-charcoal text-sm leading-relaxed">
                  Leaders and staff leave with a <strong>common language</strong> around AI and a shared framework for responsible use.
                </p>
              </div>

              <div className="bg-[#f8f8f9] border border-soft-gray/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-navy/10 rounded-lg">
                    <i className="ri-group-line text-navy text-xl"></i>
                  </div>
                  <h4 className="text-navy font-bold text-lg">For 10 Attendees</h4>
                </div>
                <p className="text-charcoal text-sm leading-relaxed">
                  Pricing starts at <strong>$3,000 for up to 10 attendees</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why a Full Day */}
      <section className="py-20 bg-[#f8f8f9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
              Why a <span className="text-gold">Full Day?</span>
            </h2>
            <p className="text-charcoal text-lg max-w-2xl mx-auto">
              The extra time makes a meaningful difference in depth and outcomes
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-7 border border-soft-gray/30 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl mb-5">
                <i className="ri-chat-3-line text-gold text-2xl"></i>
              </div>
              <h4 className="text-navy font-bold mb-2">Deeper Discussion</h4>
              <p className="text-charcoal text-sm leading-relaxed">
                More time to explore nuances, address concerns, and work through real scenarios your team faces
              </p>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-soft-gray/30 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl mb-5">
                <i className="ri-hand-heart-line text-gold text-2xl"></i>
              </div>
              <h4 className="text-navy font-bold mb-2">More Hands-On Practice</h4>
              <p className="text-charcoal text-sm leading-relaxed">
                Extended exercises and guided practice so concepts truly stick and become second nature
              </p>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-soft-gray/30 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl mb-5">
                <i className="ri-links-line text-gold text-2xl"></i>
              </div>
              <h4 className="text-navy font-bold mb-2">Cross-Role Alignment</h4>
              <p className="text-charcoal text-sm leading-relaxed">
                Shared understanding across leadership and staff so everyone is on the same page
              </p>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-soft-gray/30 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl mb-5">
                <i className="ri-question-answer-line text-gold text-2xl"></i>
              </div>
              <h4 className="text-navy font-bold mb-2">Space for Q&amp;A</h4>
              <p className="text-charcoal text-sm leading-relaxed">
                Address questions, misconceptions, and concerns that often go unresolved in shorter sessions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
              What’s <span className="text-gold">Included</span>
            </h2>
            <p className="text-charcoal text-lg max-w-2xl mx-auto">
              Everything your team needs for a comprehensive AI education
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="flex items-start gap-4 bg-[#f8f8f9] rounded-xl p-6 border border-soft-gray/20">
              <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-presentation-line text-gold text-xl"></i>
              </div>
              <div>
                <h4 className="text-navy font-bold mb-1">7 Hours of Interactive Training</h4>
                <p className="text-charcoal text-sm leading-relaxed">
                  Guided instruction with real‑world examples tailored to your industry
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-[#f8f8f9] rounded-xl p-6 border border-soft-gray/20">
              <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-book-open-line text-gold text-xl"></i>
              </div>
              <div>
                <h4 className="text-navy font-bold mb-1">AI Prompt Reference Guide</h4>
                <p className="text-charcoal text-sm leading-relaxed">
                  A comprehensive guide your team can reference long after the workshop
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-[#f8f8f9] rounded-xl p-6 border border-soft-gray/20">
              <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-lightbulb-line text-gold text-xl"></i>
              </div>
              <div>
                <h4 className="text-navy font-bold mb-1">Demo Examples</h4>
                <p className="text-charcoal text-sm leading-relaxed">
                  Real demonstrations so your team can revisit and practice independently
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-[#f8f8f9] rounded-xl p-6 border border-soft-gray/20">
              <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-discuss-line text-gold text-xl"></i>
              </div>
              <div>
                <h4 className="text-navy font-bold mb-1">Extended Guided Discussion</h4>
                <p className="text-charcoal text-sm leading-relaxed">
                  Deep‑dive conversations around your organization’s specific workflows
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-[#f8f8f9] rounded-xl p-6 border border-soft-gray/20">
              <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-shield-check-line text-gold text-xl"></i>
              </div>
              <div>
                <h4 className="text-navy font-bold mb-1">Responsible AI Framework</h4>
                <p className="text-charcoal text-sm leading-relaxed">
                  Clear guidelines on appropriate use, limitations, and best practices
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-[#f8f8f9] rounded-xl p-6 border border-soft-gray/20">
              <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-user-voice-line text-gold text-xl"></i>
              </div>
              <div>
                <h4 className="text-navy font-bold mb-1">Common Language Building</h4>
                <p className="text-charcoal text-sm leading-relaxed">
                  Shared vocabulary and understanding across all roles and levels
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compare / CTA */}
      <section className="py-20 bg-[#f8f8f9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-soft-gray/30 shadow-sm flex flex-col">
              <div className="mb-6">
                <div className="text-sm text-gold font-semibold mb-2">Shorter on Time?</div>
                <h3 className="text-2xl font-bold text-navy mb-3">Consider the Half Day Workshop</h3>
                <p className="text-charcoal leading-relaxed">
                  If your team needs a focused introduction without committing to a full day, the Half Day Workshop delivers 3.5 hours of practical, hands‑on AI training.
                </p>
              </div>
              <div className="mt-auto">
                <Link
                  to="/workshops/half-day"
                  className="inline-flex items-center gap-2 text-navy font-semibold hover:text-gold transition-colors cursor-pointer"
                >
                  View Half Day Workshop
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            <div className="bg-gradient-to-br from-navy to-[#062299] rounded-2xl p-8 flex flex-col">
              <div className="mb-6">
                <div className="text-sm text-gold font-semibold mb-2">Ready to Get Started?</div>
                <h3 className="text-2xl font-bold text-white mb-3">Book Your Full Day Workshop</h3>
                <p className="text-white/80 leading-relaxed">
                  Make AI a consistent advantage for your team. Book your full-day workshop now and transform how work gets done.
                </p>
              </div>
              <div className="mt-auto">
                <a
                  href="https://calendly.com/evernorthai/full-day-workshop"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-3.5 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap"
                >
                  Book This Workshop
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
